import { useAuth } from "@/contexts/AuthContext";
import { Target, Sparkles, Briefcase, Bot, Zap, Mail, Lock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";

export default function Login() {
  const { loginWithGoogle, loginWithPassword, registerWithPassword } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setLoading(true);
    try {
      if (mode === "signin") {
        await loginWithPassword(email, password);
        navigate("/dashboard");
      } else if (mode === "signup") {
        await registerWithPassword(name, email, password);
        navigate("/dashboard");
      } else {
        const r = await api.post("/auth/forgot-password", { email });
        setInfo(r.data.email_sent === false
          ? "If an account exists, a reset link was generated. (Email delivery isn't configured yet — contact support.)"
          : "If an account exists for this email, a reset link has been sent. Check your inbox.");
      }
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setErr(""); setInfo(""); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(139,92,246,0.35), transparent 40%), radial-gradient(circle at 80% 80%, rgba(34,211,238,0.25), transparent 40%), #0a0a1a",
        }}>
        <div className="flex items-center gap-3" data-testid="brand-large">
          <img src="/logo.png" alt="Curcompass logo" className="w-12 h-12 rounded-xl object-cover" />
          <div>
            <div className="font-bold text-2xl text-white">Curcompass</div>
            <div className="text-[11px] tracking-[0.2em] text-violet-300">AI CAREER COPILOT</div>
          </div>
        </div>

        <div>
          <h2 className="text-4xl font-bold text-white leading-tight">
            Your AI agent team,<br />
            <span className="jr-gradient-text">hunting jobs for you 24/7.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-md">
            Multi-agent system powered by Claude Sonnet 4.5 — parses your CV, scrapes LinkedIn/Indeed/Glassdoor,
            tailors your resume, and applies with your permission.
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
            {[
              { i: Bot, t: "6 Specialized Agents" },
              { i: Briefcase, t: "Multi-source Scraping" },
              { i: Sparkles, t: "AI Resume Tailoring" },
              { i: Zap, t: "Auto-Apply with Approval" },
            ].map((f) => (
              <div key={f.t} className="jr-card flex items-center gap-3 p-3">
                <f.i className="w-4 h-4 text-violet-300" />
                <span className="text-sm text-zinc-300">{f.t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-zinc-500">Built with React, FastAPI, Claude Sonnet 4.5 &amp; Apify</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Curcompass logo" className="w-10 h-10 rounded-xl object-cover" />
            <div className="font-bold text-xl text-white">Curcompass</div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset password" : "Welcome back"}
          </h1>
          <p className="text-zinc-400 mb-6">
            {mode === "signup" ? "Sign up to launch your AI agents." : mode === "forgot" ? "We'll email you a reset link." : "Sign in to launch your AI agents."}
          </p>

          {mode !== "forgot" && (
            <>
              <button
                data-testid="google-login-btn"
                onClick={loginWithGoogle}
                className="w-full flex items-center justify-center gap-3 bg-white text-zinc-900 font-semibold py-3 rounded-xl hover:bg-zinc-100 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" /></svg>
                <span>Continue with Google</span>
              </button>

              <div className="flex items-center gap-3 my-5 text-xs text-zinc-500">
                <div className="h-px flex-1 bg-white/10" />
                <span>OR</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <div className="relative">
                <User className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
                <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Full name" className="jr-input !pl-10" />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
              <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" className="jr-input !pl-10" />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-3.5" />
                <input data-testid="auth-password-input" type="password" required minLength={mode === "signup" ? 8 : 1}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"} className="jr-input !pl-10" />
              </div>
            )}

            {err && <div className="text-rose-300 text-sm" data-testid="auth-error">{err}</div>}
            {info && <div className="text-emerald-300 text-sm" data-testid="auth-info">{info}</div>}

            <button type="submit" disabled={loading} className="jr-btn-primary w-full py-3"
              data-testid={mode === "signup" ? "signup-form-submit-button" : mode === "forgot" ? "forgot-submit-btn" : "login-form-submit-button"}>
              {loading ? "Please wait..." : mode === "signup" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Sign In"}
            </button>
          </form>

          <div className="flex items-center justify-between mt-4 text-sm">
            {mode === "signin" && (
              <>
                <button onClick={() => switchMode("forgot")} className="text-zinc-400 hover:text-white" data-testid="forgot-password-link">
                  Forgot password?
                </button>
                <button onClick={() => switchMode("signup")} className="text-violet-300 hover:text-violet-200" data-testid="toggle-signup">
                  Create account
                </button>
              </>
            )}
            {mode !== "signin" && (
              <button onClick={() => switchMode("signin")} className="text-violet-300 hover:text-violet-200" data-testid="toggle-signin">
                ← Back to sign in
              </button>
            )}
          </div>

          <p className="text-xs text-zinc-500 mt-6 text-center">
            By continuing you agree to our Terms &amp; Privacy.
          </p>
        </div>
      </div>
    </div>
  );
}
